import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/ui/text";
import Spinner from "@/components/ui/spinner";
import IconContainer from "@/components/ui/icon-container";
import { Button } from "@/components/ui/button";
import { Add } from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { ProposalRowActions, ProposalsTable } from "./ProposalsTable";
import { ProposalsEmptyState } from "./ProposalsEmptyState";
import { Proposal } from "./types";
import { isActive, isRegenerable } from "./draftActions";
import { ProposalOutOfDateBanner } from "./ProposalOutOfDateBanner";

interface Props extends ProposalRowActions {
  proposals: Proposal[];
  isLoading?: boolean;
  busyIds?: ReadonlySet<string>;
  onGenerate: () => void;
  isGenerating?: boolean;
  // Why a proposal can't be built from the contract yet; disables Generate and
  // Regenerate.
  termsBlockedReason?: string;
  // Holds Regenerate until the contract's Terms & Services are known.
  termsLoading?: boolean;
  // The list couldn't be read; the rep can ask again.
  loadError?: boolean;
  // The e-sign connection is down, so open proposals show their last status.
  trackingPaused?: boolean;
  onRetry?: () => void;
  className?: string;
}

export const ProposalsPanel: React.FC<Props> = ({
  proposals,
  isLoading = false,
  busyIds,
  onGenerate,
  isGenerating,
  termsBlockedReason,
  termsLoading,
  loadError = false,
  trackingPaused,
  onRetry,
  className,
  ...rowActions
}) => {
  const { t } = useTranslation();
  // Dismissing hides the notice for this proposal until the page is left.
  const [dismissedId, setDismissedId] = useState<string>();
  const outOfDate = proposals.find(
    (proposal) => isRegenerable(proposal) && proposal.outOfDate
  );

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-6">
          <Spinner size="md" />
        </div>
      );
    }
    if (loadError) {
      return (
        <div
          className="flex items-center justify-between gap-4 p-4 rounded-2xl border border-border-border1 bg-surface-widget-card"
          role="alert"
          data-test="proposals-load-error"
        >
          <Text variant="bodySecondary">
            {t("contracts.proposals.loadError")}
          </Text>
          <Button variant="secondary" onClick={onRetry}>
            {t("contracts.proposals.retry")}
          </Button>
        </div>
      );
    }
    if (proposals.length === 0) {
      return (
        <ProposalsEmptyState
          onGenerate={onGenerate}
          isGenerating={isGenerating}
          blockedReason={termsBlockedReason}
        />
      );
    }
    return (
      <div className="bg-surface-widget-card border border-border-border1 rounded-2xl overflow-hidden">
        <ProposalsTable
          proposals={proposals}
          busyIds={busyIds}
          regenerateBlockedReason={termsBlockedReason}
          regenerateHeld={termsLoading}
          trackingPaused={trackingPaused}
          {...rowActions}
        />
        {/* Once none is in play, e.g. the draft cut after a void was deleted. */}
        {proposals.some(isActive) ? null : (
          <div className="flex items-center h-12 px-3">
            <Button
              variant="tertiary"
              onClick={onGenerate}
              disabled={isGenerating || !!termsBlockedReason}
              tooltipText={termsBlockedReason}
              data-test="proposals-generate-footer"
            >
              <IconContainer color="cta" size="5">
                <Add />
              </IconContainer>
              {t("contracts.proposals.generate")}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn("flex flex-col gap-4 min-w-0", className)}
      data-test="proposals-panel"
    >
      <div className="flex items-center h-8">
        <Text variant="lgSubtitleH1">{t("contracts.proposals.title")}</Text>
      </div>
      {outOfDate && outOfDate.id !== dismissedId && !isLoading && !loadError ? (
        <ProposalOutOfDateBanner
          onRegenerate={() => rowActions.onRegenerate(outOfDate)}
          onDismiss={() => setDismissedId(outOfDate.id)}
          blockedReason={termsBlockedReason}
          disabled={termsLoading || !!busyIds?.has(outOfDate.id)}
        />
      ) : null}
      {renderBody()}
    </div>
  );
};
